// Kill leaderboard screen - shows gladiator knockout counts for the season

import { Canvas } from '../Canvas';
import { CONSTANTS, ScreenState } from '../types';
import { Screen, ScreenContext } from './Screen';
import { GameState } from '../systems/GameState';
import { GAME_CONSTANTS } from '../data/constants';
import { GladiatorData } from '../entities/Gladiator';
import { ScrollableList } from '../components/ScrollableList';

interface LeaderboardEntry {
  gladiator: GladiatorData;
  teamName: string;
  isPlayerTeam: boolean;
}

export class KillLeaderboard implements Screen {
  readonly state = ScreenState.KILL_LEADERBOARD;
  private context: ScreenContext;
  private gameState: GameState | null = null;
  private leaderboardList: ScrollableList<LeaderboardEntry>;

  constructor(context: ScreenContext) {
    this.context = context;
    this.leaderboardList = new ScrollableList<LeaderboardEntry>({
      x: 150,
      y: 80,
      width: 724,
      height: 520,
      itemHeight: 50,
      header: 'Kauden parhaat tappajat',
      style: {
        backgroundColor: '#1a0a1a',
        borderColor: '#8a4a8a',
        headerColor: '#ffcc00',
        selectedColor: '#3a1a3a',
        scrollButtonColor: '#6a3a6a',
        scrollButtonDisabledColor: '#2a1a2a',
        scrollButtonTextColor: '#ffffff',
        positionTextColor: '#888888',
      },
      renderItem: (canvas, entry, x, y, width, _height, _isSelected) => {
        this.renderLeaderboardEntry(canvas, entry, x, y, width);
      },
    });
  }

  setGameState(gameState: GameState): void {
    this.gameState = gameState;
  }

  enter(): void {
    console.log('Entering KillLeaderboard');
    this.buildLeaderboard();
  }

  exit(): void {
    console.log('Exiting KillLeaderboard');
  }

  private buildLeaderboard(): void {
    if (!this.gameState) return;

    const entries: LeaderboardEntry[] = [];

    for (const [, gladiator] of this.gameState.gladiators) {
      if (!gladiator.ownerId) continue;

      const team = this.gameState.teams.get(gladiator.ownerId);
      if (!team) continue;

      entries.push({
        gladiator,
        teamName: team.name,
        isPlayerTeam: this.gameState.playerTeamIds.includes(team.id),
      });
    }

    // Sort by takeOuts1 (this season's kills) descending
    entries.sort((a, b) => b.gladiator.takeOuts1 - a.gladiator.takeOuts1);

    this.leaderboardList.setItems(
      entries.map((entry, idx) => ({ id: `entry-${idx}`, data: entry }))
    );
    this.leaderboardList.reset();
  }

  private renderLeaderboardEntry(canvas: Canvas, entry: LeaderboardEntry, x: number, y: number, width: number): void {
    const gladiator = entry.gladiator;

    // Get actual rank from position in list
    const items = Array.from(this.gameState?.gladiators.values() ?? [])
      .filter(g => g.ownerId)
      .sort((a, b) => b.takeOuts1 - a.takeOuts1);
    const actualRank = items.findIndex(g => g.id === gladiator.id) + 1;

    // Rank color (gold for 1st, silver for 2nd, bronze for 3rd)
    let rankColor = '#ffffff';
    if (actualRank === 1) rankColor = '#ffd700';
    else if (actualRank === 2) rankColor = '#c0c0c0';
    else if (actualRank === 3) rankColor = '#cd7f32';

    // Rank number
    canvas.drawText(`${actualRank}.`, x + 10, y + 15, CONSTANTS.GLADIATOR_NAME_FONT, rankColor);

    // Portrait
    const portrait = this.context.assets.gladiators[gladiator.race.id];
    if (portrait) {
      canvas.drawImage(portrait, x + 45, y + 3, 44, 44);
    }

    // Name and race
    const nameColor = entry.isPlayerTeam ? '#88ff88' : '#ffffff';
    canvas.drawText(gladiator.name, x + 100, y + 5, CONSTANTS.GLADIATOR_NAME_FONT, nameColor);
    canvas.drawText(gladiator.race.name, x + 100, y + 25, CONSTANTS.MSG_FONT, '#aaaaaa');

    // Team name
    canvas.drawText(entry.teamName, x + 300, y + 15, CONSTANTS.MAIN_FONT, '#aaaaff');

    // Kill count (this season)
    canvas.drawText('Kaatoja:', x + width - 150, y + 5, CONSTANTS.MSG_FONT, '#888888');
    canvas.drawText(`${gladiator.takeOuts1}`, x + width - 150, y + 22, CONSTANTS.GLADIATOR_NAME_FONT, '#ff6666');

    // Total kills
    canvas.drawText('Yhteensä:', x + width - 70, y + 5, CONSTANTS.MSG_FONT, '#888888');
    canvas.drawText(`${gladiator.takeOuts2}`, x + width - 70, y + 22, CONSTANTS.MAIN_FONT, '#ffcc00');
  }

  update(_deltaTime: number): void {}

  render(canvas: Canvas): void {
    if (!this.gameState) {
      canvas.clear(CONSTANTS.MAIN_BACKGROUND);
      return;
    }

    canvas.clear('#1a0a2a'); // Dark purple background

    // Header
    this.renderHeader(canvas);

    // Leaderboard
    this.leaderboardList.render(canvas);

    // Continue button
    this.renderContinueButton(canvas);

    // Info about season reward
    this.renderRewardInfo(canvas);
  }

  private renderHeader(canvas: Canvas): void {
    canvas.drawRect(0, 0, CONSTANTS.CANVAS_WIDTH, 55, '#3a1a3a');
    canvas.drawText('Tappajalista', CONSTANTS.CANVAS_WIDTH / 2, 10, CONSTANTS.BIG_FONT, '#ffcc00', 'center');

    canvas.drawText(
      `Kausi ${this.gameState!.year} ${GAME_CONSTANTS.YEAR_SUFFIX}`,
      CONSTANTS.CANVAS_WIDTH / 2,
      32,
      CONSTANTS.MAIN_FONT,
      '#cc88cc',
      'center'
    );
  }

  private renderRewardInfo(canvas: Canvas): void {
    const infoY = CONSTANTS.CANVAS_HEIGHT - 95;
    canvas.drawRect(150, infoY, 724, 30, '#2a1a2a');
    canvas.drawText(
      `Kauden parhaan tappajan omistaja saa ${GAME_CONSTANTS.KILL_LEADER_BONUS} kr bonuksen!`,
      CONSTANTS.CANVAS_WIDTH / 2,
      infoY + 8,
      CONSTANTS.MAIN_FONT,
      '#ffcc00',
      'center'
    );
  }

  private renderContinueButton(canvas: Canvas): void {
    canvas.drawButton(
      'continue',
      'Jatka',
      CONSTANTS.CANVAS_WIDTH / 2 - 60,
      CONSTANTS.CANVAS_HEIGHT - 55,
      120,
      40,
      CONSTANTS.MAIN_BUTTON_FONT,
      '#ffffff',
      '#6a3a6a',
      () => {
        // Progress month and go to main menu
        if (this.context.onProgressMonth) {
          this.context.onProgressMonth();
        }
        this.context.onScreenChange(ScreenState.MAIN_MENU);
      }
    );
  }

  handleClick(x: number, y: number): void {
    console.log(`KillLeaderboard click at (${x}, ${y})`);
  }

  handleKeyDown(key: string): void {
    if (key === 'Enter' || key === ' ' || key === 'Escape') {
      if (this.context.onProgressMonth) {
        this.context.onProgressMonth();
      }
      this.context.onScreenChange(ScreenState.MAIN_MENU);
    } else {
      this.leaderboardList.handleKeyDown(key);
    }
  }
}
